import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { StagedDiligenceRequestUpload } from '../../shared/models';
import { DateUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface ViewDiligenceUploadDrawerProps {
	dataRoomGuid: string | undefined;
	upload: StagedDiligenceRequestUpload | null;
	close: () => void;
}

export const ViewDiligenceUploadDrawer: React.FC<ViewDiligenceUploadDrawerProps> = ({
	dataRoomGuid,
	upload,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const [processing, setProcessing] = React.useState<boolean>(false);

	const onDeleteUpload = () => {
		setProcessing(true);
		deleteUpload();
	};

	const { mutate: deleteUpload } = useMutation<void, unknown, void>(
		() =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificUpload(dataRoomGuid, upload?.guid)
				)
			),
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Uploads(dataRoomGuid));
				snackbar.enqueueSnackbar('Upload deleted.', { variant: 'success' });
				setProcessing(false);
				close();
			},
			onError: () => {
				setProcessing(false);
				snackbar.enqueueSnackbar('There was a problem deleting the upload.', { variant: 'error' });
			},
		}
	);

	const onCompleteUpload = () => {
		setProcessing(true);
		completeUpload();
	};

	const { mutate: completeUpload } = useMutation<void, unknown, void>(
		() =>
			getResponseData(
				api.patch<void>(
					CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificUpload(dataRoomGuid, upload?.guid)
				)
			),
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Uploads(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Upload completed.', { variant: 'success' });
				setProcessing(false);
				close();
			},
			onError: () => {
				setProcessing(false);
				snackbar.enqueueSnackbar('There was a problem completing the upload.', { variant: 'error' });
			},
		}
	);

	return (
		<CerebroDrawer open={upload != null} onClose={close}>
			<CerebroHeading size="h1" component="h1">
				Complete Diligence Request Upload
			</CerebroHeading>

			<Grid container>
				<Grid item xs={12}>
					<CerebroText>Lender: {upload?.fullName}</CerebroText>
				</Grid>
				<Grid item xs={12}>
					<CerebroText>
						Created On: {DateUtils.FormatStringAsShortDate(upload?.createdOn?.toString())}
					</CerebroText>
				</Grid>
			</Grid>

			<TableContainer sx={{ marginBottom: 3 }}>
				<Table>
					<TableHead>
						<TableCell>Title</TableCell>
						<TableCell>Folder</TableCell>
						<TableCell>Details</TableCell>
					</TableHead>
					<TableBody>
						{upload?.items.map(item => (
							<TableRow key={item.guid}>
								<TableCell>{item.title}</TableCell>
								<TableCell>{item.folderName}</TableCell>
								<TableCell>{item.details}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Grid container>
				<Grid item xs={12}>
					<CerebroPrimaryButton loading={processing} onClick={onCompleteUpload}>
						Complete Upload
					</CerebroPrimaryButton>
					<CerebroSecondaryButton loading={processing} onClick={onDeleteUpload} sx={{ marginLeft: 1 }}>
						Delete Upload
					</CerebroSecondaryButton>
				</Grid>
			</Grid>
		</CerebroDrawer>
	);
};
